import React from "react";
import FormAzucarai from "modules/client/form_azucarai";

function ClientLandingPage() {
  return (
    <div className="home-page">
      <div className="wrap">
        <header>
          <div className="header-row">
            <div className="brand">
              <img
                src="https://samedaypet.s3.amazonaws.com/ba4faeb5-cc14-4e69-8846-204001c9f41f.png"
                alt="AzucarAI logo"
                className="logo-img"
              />
            </div>
            <nav>
              <a href="#services">Services</a>
              <a href="#process">Process</a>
              <a href="#industries">Industries</a>
              <a href="#request">Contact</a>
            </nav>
            <div className="header-actions">
              <a className="login-link" href="https://dashboard.azucarai.com">
                Log In
              </a>
              <a className="cta" href="#request">Request Service</a>
            </div>
          </div>
        </header>

        <section className="hero">
          <div className="eyebrow">
            <span className="dot" />
            Yelp Advertising Partner · Google Ads Certified Partner
          </div>
          <h1>
            Marketing, media, and AI systems that turn attention into{" "}
            <span className="accent">customers.</span>
          </h1>
          <p className="sub">
            One team for SEO, social, reviews, websites, apps, paid ads, AI automation,
            AR, and content production — every channel working as a single system, not
            a stack of vendors.
          </p>
          <div className="buttons">
            <a className="btn primary" href="#request">Build My Growth Plan</a>
            <a className="btn secondary" href="#services">Explore Services</a>
          </div>
        </section>

        <section className="section" id="services">
          <div className="section-head">
            <div>
              <span className="tag-label">Services</span>
              <h2>One connected system for visibility, trust, leads, and follow-up.</h2>
            </div>
            <p>
              Each service has a clear purpose: get found, look trusted, create demand,
              convert leads, and follow up faster. Social media stays its own category,
              while photo and video sit under marketing content production so it
              doesn&apos;t read as just a photography offering.
            </p>
          </div>

          <div className="services-grid">
            <div className="scard">
              <div className="stag">SEO</div>
              <h3>SEO, AEO &amp; GEO Search Growth</h3>
              <p>For businesses that need to be found on Google, Maps, and AI-powered search.</p>
              <ul>
                <li>SEO strategy and monthly optimization</li>
                <li>Local SEO and Google Business Profile support</li>
                <li>AEO and GEO content for AI search discovery</li>
                <li>Keyword research and performance reporting</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">SM</div>
              <h3>Social Media Management</h3>
              <p>A standalone content system that keeps the brand visible, active, and trustworthy.</p>
              <ul>
                <li>Monthly social media calendars</li>
                <li>Captions, graphics, carousels, and reels</li>
                <li>Publishing, scheduling, and comment monitoring</li>
                <li>Social performance reporting</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">RV</div>
              <h3>Reviews &amp; Reputation</h3>
              <p>Protect the brand, grow positive reviews, and respond professionally across key platforms.</p>
              <ul>
                <li>Google and Facebook review monitoring</li>
                <li>Review response writing and request campaigns</li>
                <li>Escalation support for negative reviews</li>
                <li>Monthly reputation reports</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">WD</div>
              <h3>Websites &amp; Landing Pages</h3>
              <p>Modern websites built to look premium, explain services clearly, and convert visitors into leads.</p>
              <ul>
                <li>Website redesigns and landing pages</li>
                <li>Conversion-focused forms and CTAs</li>
                <li>Mobile-first design and speed improvements</li>
                <li>Tracking, analytics, and lead routing setup</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">GA</div>
              <span className="flag">Google Ads Certified</span>
              <h3>Google Ads, Yelp Ads &amp; Lead Tracking</h3>
              <p>Paid campaigns connected to tracking, landing pages, and follow-up so leads are easier to measure.</p>
              <ul>
                <li>Google Ads campaign setup and management</li>
                <li>Yelp advertising support</li>
                <li>Lead tracking, call reporting, and budget optimization</li>
              </ul>
            </div>

            <div className="scard featured">
              <div className="stag">AI</div>
              <span className="flag">Featured</span>
              <h3>AI Marketing &amp; Automation</h3>
              <p>AI tools that help businesses answer faster, qualify better, and stop losing leads.</p>
              <ul>
                <li>AI first response for Yelp and web leads</li>
                <li>AI phone and SMS assistant workflows</li>
                <li>Lead qualification, hot-lead alerts, and follow-up automations</li>
              </ul>
            </div>

            <div className="scard featured">
              <div className="stag">AR</div>
              <span className="flag">Featured</span>
              <h3>AI &amp; AR Interactive Experiences</h3>
              <p>Interactive technology that helps customers explore, understand, and engage with a brand before they buy.</p>
              <ul>
                <li>AR product previews and branded experiences</li>
                <li>QR, NFC, and interactive campaign concepts</li>
                <li>Custom AI development for internal operations</li>
              </ul>
            </div>

            <div className="scard featured">
              <div className="stag">AP</div>
              <span className="flag">Featured</span>
              <h3>Native App Development</h3>
              <p>Custom iOS and Android apps built to extend the brand and give customers a direct line to the business.</p>
              <ul>
                <li>Native iOS and Android app builds</li>
                <li>Booking, loyalty, and client portal apps</li>
                <li>Push notifications and in-app messaging</li>
                <li>App Store and Google Play launch support</li>
              </ul>
            </div>

            <div className="scard featured">
              <div className="stag">CP</div>
              <span className="flag">Featured</span>
              <h3>Marketing Content Production</h3>
              <p>Photo and video created for a marketing purpose — to support ads, websites, social, email, and follow-up campaigns.</p>
              <ul>
                <li>Trade show, booth, activation, and event content capture</li>
                <li>Team, demo, product, and customer interaction content</li>
                <li>Highlight videos, short-form clips, and social-ready edits</li>
                <li>Content organized for future campaigns, not just delivered as raw files</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">ES</div>
              <h3>Email &amp; SMS Marketing</h3>
              <p>Campaigns that keep customers informed, promote offers, and bring people back.</p>
              <ul>
                <li>Monthly newsletters and promotional campaigns</li>
                <li>Email template design and list management</li>
                <li>Automation sequences and reminders</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">CR</div>
              <h3>Creative Production</h3>
              <p>Campaign visuals and branded assets that make every channel look consistent and professional.</p>
              <ul>
                <li>Graphics, ads, flyers, posters, and branded templates</li>
                <li>Video editing and short-form content packages</li>
                <li>Print, signage, and local promotional assets</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">AN</div>
              <h3>Analytics, Tracking &amp; Reporting</h3>
              <p>Clear visibility into what&apos;s working across calls, ads, websites, reviews, and campaigns.</p>
              <ul>
                <li>Call tracking and lead source reporting</li>
                <li>Website analytics and conversion tracking</li>
                <li>Ad, SEO, social, and reputation reports</li>
              </ul>
            </div>

            <div className="scard">
              <div className="stag">LC</div>
              <h3>Lead Conversion Systems</h3>
              <p>The follow-up layer that helps businesses turn more attention into real calls, bookings, and customers.</p>
              <ul>
                <li>Landing page forms and lead routing</li>
                <li>Missed-call and inquiry follow-up workflows</li>
                <li>SMS, email, and AI response sequences</li>
                <li>Hot lead alerts and conversion strategy across web, ads, and social</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section" id="process">
          <div className="section-head">
            <div>
              <span className="tag-label">Process</span>
              <h2>A simple system clients can follow.</h2>
            </div>
            <p>Every engagement moves through the same four stages, in order.</p>
          </div>
          <div className="process">
            <div className="step">
              <div className="num">1</div>
              <h3>Audit</h3>
              <p>Review the website, search visibility, reviews, ads, content, and lead flow.</p>
            </div>
            <div className="step">
              <div className="num">2</div>
              <h3>Build</h3>
              <p>Create the pages, campaigns, automations, designs, and tracking needed to grow.</p>
            </div>
            <div className="step">
              <div className="num">3</div>
              <h3>Launch</h3>
              <p>Publish content, ads, email campaigns, review systems, and AI follow-up tools.</p>
            </div>
            <div className="step">
              <div className="num">4</div>
              <h3>Optimize</h3>
              <p>Monitor results, improve campaigns, and keep the client moving forward monthly.</p>
            </div>
          </div>
        </section>

        <section className="section" id="industries">
          <div className="section-head">
            <div>
              <span className="tag-label">Industries</span>
              <h2>Built for service businesses and event-driven brands.</h2>
            </div>
            <p>Who Azucar works with, day to day.</p>
          </div>
          <div className="industries">
            <span className="industry">Veterinary Clinics</span>
            <span className="industry">Urgent Care</span>
            <span className="industry">Home Services</span>
            <span className="industry">Auto &amp; Tires</span>
            <span className="industry">Trade Shows</span>
            <span className="industry">Exhibits</span>
            <span className="industry">HVAC</span>
            <span className="industry">Roofing</span>
            <span className="industry">Plumbing</span>
            <span className="industry">Local Businesses</span>
          </div>
        </section>

        <section className="formband" id="request">
          <div className="copybox">
            <span className="tag-label">Get started</span>
            <h2>One team for content, leads, AI, and growth.</h2>
            <p>
              Your website, app, social media, reviews, ads, AI follow-up, AR experiences,
              and content production — all connected into one system.
            </p>
            <div className="list">
              <div>More visibility through SEO, AEO, GEO, and Google Ads</div>
              <div>More trust through social media and reputation management</div>
              <div>More conversions through websites, landing pages, and automation</div>
              <div>More engagement through AI, AR, and interactive experiences</div>
              <div>More reach through native iOS and Android apps</div>
              <div>More usable marketing assets from live events, trade shows, and client activations</div>
            </div>
          </div>

          <div className="form form-azucarai-host">
            <FormAzucarai
              idTrackGoogleAds="AW-11536860637"
              id2="AOZmCJ_DhY0aEN2Lmv0q"
            />
          </div>
        </section>

        <footer>
          <div>
            <b>AzucarAI</b> / Azucar Multimedia
            <br />
            Yelp Advertising Partner · Google Ads Certified Partner
          </div>
          <div>1960 Lafayette St, Santa Clara, CA 95050</div>
        </footer>
      </div>
    </div>
  );
}

export default ClientLandingPage;